/** Inline `#…` chips in project comments that link to Hub views or the inspiration board. */

import {
  UNSORTED_FOLDER_LABEL,
  UNSORTED_FOLDER_PATH,
  flattenProjectFolders,
  projectFolderBreadcrumb,
} from './projectFileFolders';

/** `#files-<folder>` — opens the Files tab on a specific project folder. */
export type FilesFolderHashTag = `files-${string}`;

export type HubHashTag = 'board' | 'tl' | 'files' | 'cos' | FilesFolderHashTag;

export interface HubHashMentionEntry {
  id: HubHashTag;
  /** Exact token stored in plain text and shown in the chip. */
  token: string;
  menuLabel: string;
  /** Prefixes after `#` that filter the suggestion list. */
  matchKeys: string[];
  /** Set for `#files-…` entries: the project folder path (or Unsorted) the chip opens. */
  folderPath?: string;
}

const FILES_FOLDER_TAG_PREFIX = 'files-';

function folderSlug(path: string): string {
  const last = path.split('/').pop() || path;
  return last.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
}

function folderMatchKeys(slug: string, breadcrumb: string): string[] {
  const words = breadcrumb
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter(Boolean);
  return Array.from(new Set([`${FILES_FOLDER_TAG_PREFIX}${slug}`, slug, ...words, breadcrumb.toLowerCase(), 'folder']));
}

function buildFilesFolderEntries(): HubHashMentionEntry[] {
  const entries: HubHashMentionEntry[] = [];
  const unsortedSlug = 'unsorted';
  entries.push({
    id: `${FILES_FOLDER_TAG_PREFIX}${unsortedSlug}`,
    token: `#${FILES_FOLDER_TAG_PREFIX}${unsortedSlug}`,
    menuLabel: `Files › ${UNSORTED_FOLDER_LABEL}`,
    matchKeys: folderMatchKeys(unsortedSlug, UNSORTED_FOLDER_LABEL),
    folderPath: UNSORTED_FOLDER_PATH,
  });
  for (const def of flattenProjectFolders()) {
    const slug = folderSlug(def.path);
    const breadcrumb = projectFolderBreadcrumb(def.path);
    entries.push({
      id: `${FILES_FOLDER_TAG_PREFIX}${slug}`,
      token: `#${FILES_FOLDER_TAG_PREFIX}${slug}`,
      menuLabel: `Files › ${breadcrumb}`,
      matchKeys: folderMatchKeys(slug, breadcrumb),
      folderPath: def.path,
    });
  }
  return entries;
}

export const HUB_HASH_FILES_FOLDER_ENTRIES: readonly HubHashMentionEntry[] = buildFilesFolderEntries();

export const HUB_HASH_MENTION_ENTRIES: readonly HubHashMentionEntry[] = [
  { id: 'board', token: '#board', menuLabel: 'Board', matchKeys: ['board'] },
  { id: 'tl', token: '#tl', menuLabel: 'Timeline', matchKeys: ['tl', 'timeline'] },
  { id: 'files', token: '#files', menuLabel: 'Files', matchKeys: ['files', 'file'] },
  { id: 'cos', token: '#COs', menuLabel: 'Change orders', matchKeys: ['cos', 'co', 'change', 'orders'] },
  ...HUB_HASH_FILES_FOLDER_ENTRIES,
];

export const HUB_HASH_TAGS: readonly HubHashTag[] = HUB_HASH_MENTION_ENTRIES.map(e => e.id);

/** Every `#files-…` tag, in folder-tree order (Unsorted first). */
export const HUB_HASH_FILES_FOLDER_TAGS: readonly HubHashTag[] = HUB_HASH_FILES_FOLDER_ENTRIES.map(e => e.id);

const ENTRY_BY_ID = new Map<string, HubHashMentionEntry>(HUB_HASH_MENTION_ENTRIES.map(e => [e.id, e]));
const ENTRY_BY_LOWER_TOKEN = new Map<string, HubHashMentionEntry>(
  HUB_HASH_MENTION_ENTRIES.map(e => [e.token.toLowerCase(), e]),
);

export function isHubHashTag(raw: string): raw is HubHashTag {
  return ENTRY_BY_ID.has(raw);
}

export function isFilesFolderHashTag(tag: string): tag is FilesFolderHashTag {
  return tag.startsWith(FILES_FOLDER_TAG_PREFIX) && ENTRY_BY_ID.has(tag);
}

export function hubHashToken(id: HubHashTag): string {
  return ENTRY_BY_ID.get(id)?.token ?? `#${id}`;
}

export function hubHashEntry(id: HubHashTag): HubHashMentionEntry {
  const entry = ENTRY_BY_ID.get(id);
  if (!entry) throw new Error(`Unknown hub hash tag: ${id}`);
  return entry;
}

/** Project folder path a `#files-…` chip opens; `null` for every other tag. */
export function hubHashFolderPath(id: HubHashTag): string | null {
  return ENTRY_BY_ID.get(id)?.folderPath ?? null;
}

/** `#files-…` tag for a folder path (or Unsorted), when one exists. */
export function hubHashTagForFolderPath(folderPath: string | null | undefined): HubHashTag | null {
  if (!folderPath) return null;
  return HUB_HASH_FILES_FOLDER_ENTRIES.find(e => e.folderPath === folderPath)?.id ?? null;
}

export function filterHubHashMentionEntries(
  allowedIds: readonly HubHashTag[],
  query: string,
): HubHashMentionEntry[] {
  const allowed = new Set(allowedIds);
  const pool = HUB_HASH_MENTION_ENTRIES.filter(e => allowed.has(e.id));
  const q = (query || '').trim().toLowerCase();
  if (!q) return [...pool];
  const starts = pool.filter(entry => entry.matchKeys.some(key => key.startsWith(q)));
  const contains = pool.filter(
    entry => !starts.includes(entry) && entry.matchKeys.some(key => key.includes(q)),
  );
  return [...starts, ...contains];
}

/** Split plain comment text into segments, keeping hash tokens as separate parts. */
export const HUB_HASH_PLAIN_SPLIT_RE = /(#files-[a-z0-9-]+|#board|#tl|#files|#COs)\b/i;

export function hubHashTagFromPlainToken(token: string): HubHashTag | null {
  return ENTRY_BY_LOWER_TOKEN.get(token.trim().toLowerCase())?.id ?? null;
}

export function parseHubHashFromElement(el: Element): HubHashTag | null {
  const link = el.getAttribute('data-hub-link');
  if (link && isHubHashTag(link)) return link;
  if (el.hasAttribute('data-board') || el.classList.contains('rce-board-mention')) return 'board';
  return null;
}

const INLINE_BOARD_HTML_RE = /data-board|rce-board-mention|#board\b/i;
const INLINE_BOARD_PLAIN_RE = /(^|[^\w])#board\b/i;

/** True when the comment body contains an inline `#board` chip or token. */
export function commentHasInlineBoardMention(bodyHtml?: string, body?: string): boolean {
  if (bodyHtml && INLINE_BOARD_HTML_RE.test(bodyHtml)) return true;
  if (body && INLINE_BOARD_PLAIN_RE.test(body)) return true;
  return false;
}
